import { BlockType } from '@next-space/fe-api-idl';
import type { FileWithPath } from 'file-selector';
import { textToSegments } from 'src/editor/utils/editor';
import { getMediaInfo } from 'src/hooks/utils/use-get-media-info';
import { CanAppendAttachmentBlockList } from 'src/mind-map/utils/const';
import { HISTORY_EFFECTS, TRANSACTION_FIRE } from 'src/redux/actions';
import { addBlock } from 'src/redux/managers/block/add';
import { addColumn } from 'src/redux/managers/block/add-column';
import { convertBlock } from 'src/redux/managers/block/convert';
import { updateBlock } from 'src/redux/managers/block/update';
import { cache, dispatch } from 'src/redux/store';
import { isMindMap } from 'src/utils/block-type-utils';
import { isFlowUsApp } from 'src/utils/electron-util';
import { getDisplay, getFileNameInfo } from 'src/utils/file';
import type { Position } from '../../use-dnd/types';
import type { PasteHook } from '../types';
import { common } from './common';

export const pasteFile = (
  {
    transaction,
    uploadFile,
    position,
    dropId,
    uploadFileSuccess,
  }: PasteHook & { position?: Position; dropId?: string },
  files: FileList | ArrayLike<FileWithPath | DataTransferItem>
) => {
  if (!files.length) return;

  const { pageId, pos, where, clearEmptyBlock, isInInlineEditable } = common();

  if (isInInlineEditable) return;

  transaction(() => {
    const data: { fileBlockId: string; file: File | FileWithPath; ossName?: string }[] = [];

    Array.from(files as FileWithPath[]).reduce((pre, file, index) => {
      // 先不处理文件夹
      if (!isFlowUsApp.check && file.path?.includes('/')) return pre;

      const newWhere = { ...where };

      if (dropId) {
        const parentId = cache.blocks[dropId]?.parentId || '';
        newWhere.parentId = parentId;
        if (position === 'top') {
          newWhere.before = dropId;
        }
        if (position === 'bottom') {
          newWhere.after = dropId;
        }
      } else {
        if (pos === pageId) {
          newWhere.first = true;
        } else if (pre && !newWhere.last) {
          // 有last就不需要设置after，思维导图专用
          newWhere.after = pre;
        }
      }

      const { extName } = getFileNameInfo(file.name);
      // 如果是导图并且选中文本块或者focus文本块的情况下，第一个文件上传前，需要把当前块转成图片块
      if (
        pos &&
        index === 0 &&
        cache.ui.selectedBlocks.length === 1 &&
        isMindMap(cache.blocks[pageId]?.type) &&
        CanAppendAttachmentBlockList.includes(cache.blocks[pos ?? '']?.type ?? -1)
      ) {
        convertBlock([pos], {
          type: BlockType.FILE,
          data: {
            display: 'image',
            caption: cache.blocks[pos]?.data.caption,
            size: file.size,
            segments: textToSegments(file.name),
          },
        });
        data.push({ fileBlockId: pos, file });
        return pos;
      }

      const fileBlockId = addBlock(
        {
          type: BlockType.FILE,
          data: {
            segments: textToSegments(file.name),
            size: file.size,
            extName,
            localUrl: URL.createObjectURL(file),
            display: getDisplay(extName),
          },
        },
        newWhere
      );

      data.push({ fileBlockId, file });

      return fileBlockId;
    }, pos);

    if (dropId && (position === 'left' || position === 'right')) {
      addColumn(
        data.map((o) => o.fileBlockId),
        dropId,
        position
      );
    }

    dispatch(
      HISTORY_EFFECTS({
        init() {
          data.forEach((o) => {
            void uploadFile?.({
              key: o.fileBlockId,
              file: o.file as File,
              pageId,
              blockId: o.fileBlockId,
              type: 'file',
              inTransferList: true,
              async onComplete(ret) {
                if (ret.success) {
                  const mediaInfo = await getMediaInfo(o.file as File);
                  // eslint-disable-next-line require-atomic-updates
                  o.ossName = ret.ossName;
                  updateBlock(o.fileBlockId, {
                    data: { ossName: o.ossName, ...mediaInfo },
                  });
                  dispatch(TRANSACTION_FIRE());
                  uploadFileSuccess?.();
                }
              },
            });
          });
        },
        redo() {
          data.forEach((o) => {
            if (o.ossName) {
              updateBlock(o.fileBlockId, { data: { ossName: o.ossName } });
              dispatch(TRANSACTION_FIRE());
            }
          });
        },
        undo() {},
      })
    );

    clearEmptyBlock();
  });
};
